import { bidApiService } from './bid-api-service';

export interface Project {
  id: string;
  name: string;
  description?: string;
  address?: string;
}

export class ProjectMatchingService {
  private static instance: ProjectMatchingService;
  private projects: Project[] = [];
  private lastFetch: number = 0;
  private readonly CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

  private constructor() {}

  public static getInstance(): ProjectMatchingService {
    if (!ProjectMatchingService.instance) {
      ProjectMatchingService.instance = new ProjectMatchingService();
    }
    return ProjectMatchingService.instance;
  }

  /**
   * Get all available projects
   */
  public async getProjects(): Promise<Project[]> {
    const now = Date.now();
    
    // Return cached projects if still fresh
    if (this.projects.length > 0 && (now - this.lastFetch) < this.CACHE_DURATION) {
      return this.projects;
    }

    try {
      const projects = await bidApiService.getProjects();
      this.projects = projects.map(p => ({
        id: p.id,
        name: p.name,
        description: undefined,
        address: undefined
      }));
      this.lastFetch = now;
      return this.projects;
    } catch (error) {
      console.error('[Project Matching] Failed to fetch projects:', error);
      return [];
    }
  }

  /**
   * Match a project name from bid text to an existing project
   */
  public async matchProject(bidText: string, extractedProjectName?: string): Promise<Project | null> {
    try {
      const projects = await this.getProjects();
      
      if (projects.length === 0) {
        return null;
      }

      // If we have an extracted project name, try to match it first
      if (extractedProjectName) {
        const exactMatch = this.findExactMatch(extractedProjectName, projects);
        if (exactMatch) return exactMatch;

        const fuzzyMatch = this.findFuzzyMatch(extractedProjectName, projects);
        if (fuzzyMatch) return fuzzyMatch;
      }

      // If no extracted name or no match, search in the full bid text
      const textMatch = this.findProjectInText(bidText, projects);
      return textMatch;

    } catch (error) {
      console.error('[Project Matching] Failed to match project:', error);
      return null;
    }
  }

  /**
   * Find exact match for project name
   */
  private findExactMatch(projectName: string, projects: Project[]): Project | null {
    const normalizedName = this.normalizeText(projectName);
    
    return projects.find(project => 
      this.normalizeText(project.name) === normalizedName
    ) || null;
  }

  /**
   * Find fuzzy match for project name
   */
  private findFuzzyMatch(projectName: string, projects: Project[]): Project | null {
    const normalizedName = this.normalizeText(projectName);
    
    // Find projects that contain the extracted name or vice versa
    const matches = projects.filter(project => {
      const normalizedProjectName = this.normalizeText(project.name);
      
      return normalizedProjectName.includes(normalizedName) || 
             normalizedName.includes(normalizedProjectName);
    });

    // Return the best match (shortest name that contains the search term)
    if (matches.length > 0) {
      return matches.reduce((best, current) => 
        current.name.length < best.name.length ? current : best
      );
    }

    return null;
  }

  /**
   * Find project mentioned in the full bid text
   */
  private findProjectInText(bidText: string, projects: Project[]): Project | null {
    const normalizedText = this.normalizeText(bidText);
    
    // Look for project names in the text
    const matches = projects.filter(project => {
      const normalizedProjectName = this.normalizeText(project.name);
      
      // Split project name into words for better matching
      const projectWords = normalizedProjectName.split(/\s+/);
      
      // Check if most words from project name appear in the text
      const matchingWords = projectWords.filter(word => 
        word.length > 2 && normalizedText.includes(word)
      );
      
      // Require at least 70% of words to match
      return matchingWords.length >= Math.ceil(projectWords.length * 0.7);
    });

    // Return the best match (most words matched)
    if (matches.length > 0) {
      return matches.reduce((best, current) => {
        const bestWords = this.normalizeText(best.name).split(/\s+/);
        const currentWords = this.normalizeText(current.name).split(/\s+/);
        
        const bestMatches = bestWords.filter(word => 
          word.length > 2 && normalizedText.includes(word)
        ).length;
        
        const currentMatches = currentWords.filter(word => 
          word.length > 2 && normalizedText.includes(word)
        ).length;
        
        return currentMatches > bestMatches ? current : best;
      });
    }

    return null;
  }

  /**
   * Normalize text for comparison
   */
  private normalizeText(text: string): string {
    return text
      .toLowerCase()
      .replace(/[^\w\s]/g, '') // Remove punctuation
      .replace(/\s+/g, ' ') // Normalize whitespace
      .trim();
  }
}

export const projectMatchingService = ProjectMatchingService.getInstance();
